<template>
	<section class="section-job-categories py-8 md:py-20 relative">
		<div
			class="hidden lg:block absolute left-0 top-1/2 translate-y-[-50%] w-[220px] h-[260px] bg-[url('https://res.cloudinary.com/djbs/image/upload/v1653298940/content/DreamJobs/Graphics/pic_job_search.svg')] bg-cover bg-no-repeat"
		></div>
		<ResponsiveContainer class="relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto">
			<div class="flex flex-col items-start md:items-end pr-0 md:pr-20 w-full">
				<div class="pl-0 md:pl-24">
					<BaseHeading type="h2" class="my-3 col-span-full relative">
						<span
							class="inline-block md:block"
							:class="globalConfig.isBlue ? 'text-dj-blue' : 'text-dj-red'"
						>
							{{ $t("jobCategories.title1") }}&nbsp;
						</span>
						<span class="text-dj-red md:text-dj-black">{{ $t("jobCategories.title2") }}</span>
					</BaseHeading>
					<p class="text-xl font-normal mb-6 md:mb-0" v-html="$t('jobCategories.subtitle')"></p>
				</div>
			</div>
			<div class="w-full md:pr-20">
				<ul class="block">
					<li v-for="(category, index) in categoriesList" :key="category.id" class="pr-2 pb-2 inline-block">
						<button @click="toggleCategory(index)">
							<BaseTagPill
								v-if="category.selected == true && globalConfig.isBlue"
								type="grey"
								class="font-medium hover:text-dj-blue hover:sm:text-dj-black"
							>
								{{ category.name[locale] }}
							</BaseTagPill>
							<BaseTagPill
								v-else-if="category.selected == true"
								type="pink"
								class="font-medium hover:text-dj-red hover:sm:text-dj-black"
							>
								{{ category.name[locale] }}
							</BaseTagPill>
							<BaseTagPill
								v-else-if="globalConfig.isBlue"
								class="font-medium hover:text-dj-gray-dark hover:sm:text-dj-blue"
							>
								{{ category.name[locale] }}
							</BaseTagPill>
							<BaseTagPill v-else class="font-medium hover:text-dj-gray-dark hover:sm:text-dj-red">
								{{ category.name[locale] }}
							</BaseTagPill>
						</button>
					</li>
				</ul>
				<BaseLink :href="searchUrl" v-if="arrFavJobCategories.length > 0">
					<BaseButton v-if="globalConfig.isBlue" class="mt-4 mr-auto" type="primary" size="small">
						{{ $t("mainCta.card2.link") }}
						<BaseIcon class="w-5 h-5 ml-2 text-[24px]" color="#FFFFFF" icon="ic:outline-arrow-forward" />
					</BaseButton>
					<BaseButton v-else class="mt-4 mr-auto" type="primary-red" size="small">
						{{ $t("mainCta.card2.link") }}
						<BaseIcon class="w-5 h-5 ml-2 text-[24px]" color="#FFFFFF" icon="ic:outline-arrow-forward" />
					</BaseButton>
				</BaseLink>
			</div>
		</ResponsiveContainer>
	</section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";

import axios from "axios";

export default defineComponent({
	name: "JobCategories",
	setup(_props) {
		const { locale, t } = useI18n();

		const { $openToast } = useNuxtApp();

		const categoriesList = ref([]);
		const favJobCategories =
			Boolean(useCookie("cookieConsentAccepted").value) == true
				? useCookie("favouriteJobCategories").value
				: useState("favouriteJobCategories").value;
		const arrFavJobCategories =
			(favJobCategories != undefined &&
				favJobCategories.toString().length > 0 &&
				favJobCategories.toString().split(",")) ||
			[];

		const searchUrl = ref("");

		function updateCookie() {
			useCookie("favouriteJobCategories", { maxAge: 86400 }).value = arrFavJobCategories.toString();
		}

		function updateButtonUrl() {
			const text = arrFavJobCategories.join(",");
			searchUrl.value = `${locale.value}/jobs?category=${text}`;
		}

		function setSelectedCategories() {
			arrFavJobCategories.forEach((el) => {
				for (let index = 0; index < categoriesList.value.length; index++) {
					if (categoriesList.value[index].id == el) {
						categoriesList.value[index].selected = true;
					}
				}
			});
		}

		function addToFavJobCategories(id: number | string) {
			if (arrFavJobCategories.length < 1) {
				// Works on deploy
				try {
					$openToast({ message: t("jobCategories.categorySaved"), type: "success", description: "" });
				} catch (error) {
					console.error(error);
				}
			}

			arrFavJobCategories.push(id.toString());

			useState("favouriteJobCategories").value = arrFavJobCategories.toString();
			Boolean(useCookie("cookieConsentAccepted").value) == true && updateCookie();
			updateButtonUrl();
		}

		function removeFromFavJobCategories(id: number | string) {
			let removeIndex = null;
			arrFavJobCategories.forEach((el, ind) => {
				if (el == id) {
					removeIndex = ind;
				}
			});
			arrFavJobCategories.splice(removeIndex, 1);

			useState("favouriteJobCategories").value = arrFavJobCategories.toString();
			Boolean(useCookie("cookieConsentAccepted").value) == true && updateCookie();
			updateButtonUrl();
		}

		function toggleCategory(index: number) {
			if (categoriesList.value[index].selected) {
				removeFromFavJobCategories(categoriesList.value[index].id);
			} else {
				addToFavJobCategories(categoriesList.value[index].id);
			}

			categoriesList.value[index].selected = !categoriesList.value[index].selected;
		}

		onMounted(async () => {
			let filterScope = "/tags?type[]=job-category&scope[]=hasAttached&per_page=50";

			if (globalConfig.isBlue) {
				filterScope = "/tags?type[]=job-category&scope[]=hasAttached&scope[]=isBlue&per_page=50";
			}

			if (globalConfig.isNotBlueHU) {
				filterScope = "/tags?type[]=job-category&scope[]=hasAttached&scope[]=isNotBlue&per_page=50";
			}

			// Fetch Categories
			await axios
				.get(globalConfig.API + filterScope)
				.then((response) => {
					categoriesList.value = response.data.data;
				})
				.catch((error: any) => {
					console.error("JobCategories fetch error", error.message || error);
				});

			setSelectedCategories();
			updateButtonUrl();
		});

		return {
			locale,
			categoriesList,
			toggleCategory,
			searchUrl,
			arrFavJobCategories,
		};
	},
});
</script>
